import {Prefab} from './prefab.js';
import {
    defaultPropertyCloner,
    ComponentProperty,
    Type,
    PropertyRecord,
} from './property.js';
import {
    AnimationComponent,
    BrokenComponent,
    CollisionComponent,
    Component,
    ComponentConstructor,
    DestroyedComponentInstance,
    InputComponent,
    LightComponent,
    MeshComponent,
    Object3D,
    PhysXComponent,
    TextComponent,
    ViewComponent,
    ParticleEffectComponent,
    LogTag,
    inheritProperties,
} from './wonderland.js';

import {CBORReader, CBORType, getType, isUndefined} from './utils/cbor.js';

/** Native components list */
export interface NativeComponents {
    'collision': CollisionComponent;
    'text': TextComponent;
    'view': ViewComponent;
    'mesh': MeshComponent;
    'input': InputComponent;
    'light': LightComponent;
    'animation': AnimationComponent;
    'physx': PhysXComponent;
    'particle-effect': ParticleEffectComponent;
}

/**
 * Manage all component managers in a scene.
 *
 * @hidden
 */
export class ComponentManagers {
    /** Animation manager index. */
    readonly animation: number = -1;
    /** Collision manager index. */
    readonly collision: number = -1;
    /** JavaScript manager index. */
    readonly js: number = -1;
    /** Physx manager index. */
    readonly physx: number = -1;
    /** View manager index. */
    readonly view: number = -1;

    /**
     * Component class instances per type to avoid GC.
     *
     * @note Maps the manager index to the list of components.
     *
     * @todo: Refactor ResourceManager and re-use for components.
     */
    private readonly _cache: (Component | null)[][] = [];
    /** Manager index to component class. */
    private readonly _constructors: ComponentConstructor[];
    /* Manager name to the manager index. */
    private readonly _nativeManagers: Map<string, number> = new Map();

    /** Host instance. */
    private readonly _scene: Prefab;

    constructor(scene: Prefab) {
        this._scene = scene;
        const wasm = this._scene.engine.wasm;

        const native = [
            AnimationComponent,
            CollisionComponent,
            InputComponent,
            LightComponent,
            MeshComponent,
            PhysXComponent,
            TextComponent,
            ViewComponent,
            ParticleEffectComponent,
        ];
        this._cache = new Array(native.length);
        this._constructors = new Array(native.length);

        for (const Class of native) {
            const ptr = wasm.tempUTF8(Class.TypeName);
            const manager = wasm._wl_scene_get_component_manager_index(scene._index, ptr);
            this._constructors[manager] = Class;
            this._cache[manager] = [] as Component[];
            this._nativeManagers.set(Class.TypeName, manager);
        }

        this.animation = this._nativeManagers.get(AnimationComponent.TypeName)!;
        this.collision = this._nativeManagers.get(CollisionComponent.TypeName)!;
        this.physx = this._nativeManagers.get(PhysXComponent.TypeName)!;
        this.view = this._nativeManagers.get(ViewComponent.TypeName)!;

        const ptr = wasm.tempUTF8('js');
        this.js = wasm._wl_scene_get_component_manager_index(scene._index, ptr);
        this._cache[this.js] = [] as Component[];
    }

    createJs(index: number, id: number, type: number, object: number) {
        const wasm = this._scene.engine.wasm;
        const ctor = wasm._componentTypes[type];
        if (!ctor) {
            throw new Error(`Type index ${type} isn't registered`);
        }

        const log = this._scene.engine.log;

        let component = null;
        try {
            component = new ctor(this._scene, this.js, id);
        } catch (e) {
            log.error(
                LogTag.Component,
                `Exception during instantiation of component ${ctor.TypeName}`
            );
            log.error(LogTag.Component, e);
            component = new BrokenComponent(this._scene);
        }
        component._object = this._scene.wrap(object);

        try {
            component.resetProperties();
        } catch (e) {
            log.error(
                LogTag.Component,
                `Exception during ${component.type} resetProperties() on object ${component.object.name}`
            );
            log.error(LogTag.Component, e);
        }

        this._scene._jsComponents[index] = component;

        /* Add to cache. This is required because destruction is
         * ID-based and not index-based. */
        this._cache[this.js][id] = component;

        return component;
    }

    /**
     * Get components of type T.
     *
     * @param type Component class.
     * @returns An array containing all components in this scene.
     */
    components<T extends Component>(type: ComponentConstructor<T>, active: boolean): T[] {
        return this.componentsFromTypename(type.TypeName, active) as T[];
    }

    /**
     * Similar to {@link getComponents}, but directly from string.
     *
     * @param typename The component `TypeName` attribute.
     */
    componentsFromTypename(typename: string, active: boolean): Component[] {
        const wasm = this._scene.engine.wasm;

        const manager = this.getNativeManager(typename) ?? this.js;
        const subTypeIndex =
            manager === this.js ? wasm._componentTypeIndices[typename] ?? 0 : 0;

        const maxRead = wasm._tempMemSize / 4;
        const components: Component[] = [];
        let read = 0;
        let offset = 0;
        while (
            (read = wasm._wl_scene_get_components(
                this._scene._index,
                manager,
                subTypeIndex,
                active,
                offset,
                maxRead,
                wasm._tempMem
            ))
        ) {
            for (let i = 0; i < read; ++i) {
                const id = wasm._tempMemUint32[i];
                components.push(this.wrapAny(manager, id) as Component);
            }
            /* Offset by `maxRead` because js components of different types are contiguous.
             * `read` could be less than `maxRead`, and we want to skip all
             * the components already processed. */
            offset += maxRead;
        }

        return components;
    }

    /**
     * Get component of type T.
     *
     * @note Currently only works for native components, but we will extend this later (and expose it in the API)
     */
    componentAt<T extends Component>(type: ComponentConstructor<T>, index: number): T {
        const wasm = this._scene.engine.wasm;
        const manager = this.getNativeManager(type.TypeName)!;
        const id = wasm._wl_scene_get_component(this._scene._index, manager, index);
        return this.wrapAny(manager, id) as T;
    }

    /**
     * Retrieve a cached component.
     *
     * @param manager The manager index.
     * @param id The component id.
     * @returns The component if cached, `null` otherwise.
     */
    get(manager: number, id: number) {
        return this._cache[manager][id] ?? null;
    }

    /**
     * Wrap the animation.
     *
     * @param id Id to wrap.
     * @returns The previous instance if it was cached, or a new one.
     */
    wrapAnimation(id: number): AnimationComponent {
        return this.wrapNative(this.animation, id) as AnimationComponent;
    }

    /**
     * Wrap the collision.
     *
     * @param id Id to wrap.
     * @returns The previous instance if it was cached, or a new one.
     */
    wrapCollision(id: number): CollisionComponent {
        return this.wrapNative(this.collision, id) as CollisionComponent;
    }

    /**
     * Wrap the view.
     *
     * @param id Id to wrap.
     * @returns The previous instance if it was cached, or a new one.
     */
    wrapView(id: number): ViewComponent {
        return this.wrapNative(this.view, id) as ViewComponent;
    }

    /**
     * Wrap the physx.
     *
     * @param id Id to wrap.
     * @returns The previous instance if it was cached, or a new one.
     */
    wrapPhysx(id: number): PhysXComponent {
        return this.wrapNative(this.physx, id) as PhysXComponent;
    }

    /**
     * Retrieves a component instance if it exists, or create and cache
     * a new one.
     *
     * @note This api is meant to be used internally. Please have a look at
     * {@link Object3D.addComponent} instead.
     *
     * @param componentType Component manager index
     * @param componentId Component id in the manager
     *
     * @returns JavaScript instance wrapping the native component
     */
    wrapNative(manager: number, id: number) {
        if (id < 0) return null;

        const cache = this._cache[manager];
        if (cache[id]) return cache[id];

        const scene = this._scene;
        const Class = this._constructors[manager];
        const component = new Class(scene, manager, id);
        cache[id] = component;
        return component;
    }

    /**
     * Wrap a native or js component.
     *
     * @throws For JavaScript components that weren't previously cached,
     * since that would be a bug in the runtime / api.
     *
     * @param manager The manager index.
     * @param id The id to wrap.
     * @returns The previous instance if it was cached, or a new one.
     */
    wrapAny(manager: number, id: number) {
        if (id < 0) return null;

        if (manager === this.js) {
            const found = this._cache[this.js][id];
            if (!found) {
                throw new Error('JS components must always be cached');
            }
            return found.constructor !== BrokenComponent ? found : null;
        }

        return this.wrapNative(manager, id);
    }

    getNativeManager(name: string): number | null {
        const manager = this._nativeManagers.get(name);
        return manager !== undefined ? manager : null;
    }

    /**
     * Perform cleanup upon component destruction.
     *
     * @param instance The instance to destroy.
     *
     * @hidden
     */
    destroy(instance: Component) {
        const localId = instance._localId;
        const manager = instance._manager;
        (instance._id as number) = -1;
        (instance._localId as number) = -1;
        (instance._manager as number) = -1;

        const erasePrototypeOnDestroy = this._scene.engine.erasePrototypeOnDestroy;
        /* Destroy the prototype of this instance to avoid using a dangling component */
        if (erasePrototypeOnDestroy && instance) {
            Object.setPrototypeOf(instance, DestroyedComponentInstance);
        }

        /* Remove from the cache to avoid side-effects when
         * re-creating a component with the same id. */
        this._cache[manager][localId] = null;
    }

    /** Number of managers **registered** in the scene, i.e., `js`, `names`, etc... */
    get managersCount() {
        return this._scene.engine.wasm._wl_scene_get_component_manager_count(
            this._scene._index
        );
    }
}

export function resetComponentProperties(record: Component) {
    const ctor = record.constructor as ComponentConstructor;
    const properties = ctor.Properties;
    if (!properties) return;
    for (const name in properties) {
        const property = properties[name];
        const cloner = property.cloner ?? defaultPropertyCloner;
        (record as Record<string, any>)[name] = cloner.clone(
            property.type,
            property.default
        );
    }
}

export function setupComponentClass(ctor: ComponentConstructor | PropertyRecord) {
    inheritProperties(ctor);
    /** @todo: Merge the _setupDefaults and _setPropertyOrder calls with this function. */
    for (const name in ctor.Properties) {
        const prop = ctor.Properties[name];
        let record = null;
        switch (prop.type) {
            case Type.Record:
                record = prop.record;
                break;
            case Type.Array:
                record = prop.element?.record;
                break;
            default:
                break;
        }
        if (record && !record._propertyOrder) setupComponentClass(record);
    }
    _setupDefaults(ctor);
    _setPropertyOrder(ctor);
}

/**
 * Decode component properties from CBOR encoded data.
 *
 * ## Failure
 *
 * When decoding properties, failures will not be treated as errors. Failures include:
 * - Invalid parameters count
 * - Wrongly defined property
 *
 * When a failure occurs, the cbor reader will be consumed to prevent
 * errors to cascade in the following components.
 */
export class ComponentPropertyDecoder {
    scene: Prefab;
    offsets: Uint32Array;

    constructor(scene: Prefab, offsets: Uint32Array) {
        this.scene = scene;
        this.offsets = offsets;
    }

    decode(cbor: CBORReader, component: Component) {
        const ctor = component.constructor as ComponentConstructor;
        if (!ctor._propertyOrder) setupComponentClass(ctor);

        const typeInfo = cbor.readTypeInfo();
        if (ctor === BrokenComponent) {
            return cbor.readItem(typeInfo); /* Consumme the cbor state */
        }

        const cborType = getType(typeInfo);
        if (cborType !== CBORType.Array) {
            this._error(`${component} parameters not encoded as an array.`);
            return cbor.readItem(typeInfo);
        }

        const paramNames = ctor._propertyOrder;
        const count = cbor.readArrayLength(typeInfo) as number;
        if (count !== paramNames.length) {
            this._error(
                `${component} has ${count} parameters encoded, but expected ${paramNames.length}`
            );
            return cbor.readItem(typeInfo, count);
        }

        for (let j = 0; j < count; ++j) {
            const name = paramNames[j];
            const property = ctor.Properties[name];
            (component as any)[name] = this.decodeProperty(cbor, name, property);
        }
    }

    decodeProperty(cbor: CBORReader, name: string, property: ComponentProperty) {
        const typeInfo = cbor.readTypeInfo();

        if (property.type === Type.Record) {
            return this.decodeRecordProperty(cbor, name, property, typeInfo);
        } else if (property.type === Type.Array) {
            return this.decodeArrayProperty(cbor, name, property, typeInfo);
        }

        let value = cbor.readItem(typeInfo);

        /* Default values are sent as undefined to avoid wasting space */
        if (value === undefined) {
            const cloner = property.cloner ?? defaultPropertyCloner;
            value = cloner.clone(property.type, property.default);
            return value;
        }
        /* This skips unset resource parameters as those are sent
         * directly with type null. Offsets for Int and Float types
         * should always be 0. */
        /** @todo CBOR tag to mark resources? Wastes a byte though. */
        if (typeof value === 'number') {
            value += this.offsets[property.type];
        }

        const engine = this.scene.engine;
        switch (property.type) {
            case Type.Bool:
            case Type.Int:
            case Type.Float:
            case Type.String:
            case Type.Enum:
            case Type.Vector2:
            case Type.Vector3:
            case Type.Vector4:
                /* Nothing to do */
                return value;
            case Type.Object: {
                const wasm = engine.wasm;
                return value
                    ? this.scene.wrap(wasm._wl_object_id(this.scene._index, value))
                    : null;
            }
            case Type.Mesh:
                return engine.meshes.wrap(value);
            case Type.Texture:
                return engine.textures.wrap(value);
            case Type.Material:
                return engine.materials.wrap(value);
            case Type.ParticleEffect:
                return engine.particleEffects.wrap(value);
            case Type.Animation:
                return this.scene.animations.wrap(value);
            case Type.Skin:
                return this.scene.skins.wrap(value);
            case Type.Color: {
                /* Colors are sent as Uint8Array. Normalize positive
                 * integer values to 0-1. Don't assume any bit size
                 * here, could become Uint16Array as well. */
                const max = (1 << (value.BYTES_PER_ELEMENT * 8)) - 1;
                return Float32Array.from(value, (f: number, _) => f / max);
            }
        }
    }

    decodeRecordProperty(
        cbor: CBORReader,
        name: string,
        property: ComponentProperty,
        typeInfo: number
    ) {
        const cborType = getType(typeInfo);
        if (cborType !== CBORType.Array && cborType !== CBORType.Constant) {
            /* For memory/performance reason, records are serialized as arrays,
             * since keys aren't needed during the decoding phase. */
            this._error(`Record parameter '${name}' not serialized as an array`);
            cbor.readItem(typeInfo);
            return null;
        }
        const record = property.record;
        if (!record) {
            this._error(`Record parameter '${name}' .record is undefined`);
            cbor.readItem(typeInfo);
            return null;
        }

        if (!record._propertyOrder) setupComponentClass(record);
        const propertyOrder = record._propertyOrder as string[];

        const count = cbor.readArrayLength(typeInfo) as number;
        if (isUndefined(cborType, count)) {
            const cloner = property.cloner ?? defaultPropertyCloner;
            return cloner.clone(property.type, property.default);
        }

        const result = new (record as any)();
        if (count !== propertyOrder.length) {
            const propsCount = propertyOrder.length;
            this._error(
                `Record parameter ${name} has ${count} sub-parameters encoded, but expected ${propsCount}`
            );
            cbor.readItem(typeInfo, count);
            return result;
        }
        const order = record._propertyOrder!;
        for (let i = 0; i < order.length; ++i) {
            const key = order[i];
            const prop = record.Properties![key];
            (result as any)[key] = this.decodeProperty(cbor, key, prop);
        }
        return result;
    }

    decodeArrayProperty(
        cbor: CBORReader,
        name: string,
        property: ComponentProperty,
        typeInfo: number
    ) {
        const log = this.scene.engine.log;
        const cborType = getType(typeInfo);
        if (cborType !== CBORType.Array && cborType !== CBORType.Constant) {
            /* For memory/performance reason, records are serialized as arrays,
             * since keys aren't needed during the decoding phase. */
            this._error(`Array parameter '${name}' not serialized as an array`);
            cbor.readItem(typeInfo);
            return [];
        }

        if (!property.element) {
            this._error(`Array parameter '${name}' .element property is undefined`);
            cbor.readItem(typeInfo);
            return [];
        }

        const count = cbor.readArrayLength(typeInfo) as number;
        if (isUndefined(cborType, count)) return [];

        const result = new Array(count);
        for (let i = 0; i < count; ++i) {
            result[i] = this.decodeProperty(cbor, name, property.element);
        }
        return result;
    }

    private _error(msg: string) {
        const log = this.scene.engine.log;
        log.error(LogTag.Engine, msg);
    }
}

/**
 * Default component parameter value per type.
 */
const _componentDefaults = new Map<Type, any>([
    [Type.Bool, false],
    [Type.Int, 0],
    [Type.Float, 0.0],
    [Type.String, ''],
    [Type.Enum, undefined],
    [Type.Object, null],
    [Type.Mesh, null],
    [Type.Texture, null],
    [Type.Material, null],
    [Type.Animation, null],
    [Type.Skin, null],
    [Type.ParticleEffect, null],
    [Type.Color, Float32Array.from([0.0, 0.0, 0.0, 1.0])],
    [Type.Vector2, Float32Array.from([0.0, 0.0])],
    [Type.Vector3, Float32Array.from([0.0, 0.0, 0.0])],
    [Type.Vector4, Float32Array.from([0.0, 0.0, 0.0, 0.0])],
    [Type.Record, undefined],
    [Type.Array, []],
]);

/**
 * Setup the defaults value of the properties on a given
 * component class.
 *
 * @param ctor The component class
 */
function _setupDefaults(ctor: ComponentConstructor | PropertyRecord) {
    for (const name in ctor.Properties) {
        const p = ctor.Properties[name];

        if (p.type === Type.Enum) {
            /* Enum default can be a string or an index. Convert to and/or
             * sanity-check the index. */
            if (p.values?.length) {
                /* Don't try to look up the default if the user specified a
                 * number or we already converted to one. */
                if (typeof p.default !== 'number') {
                    /* If undefined, missing element or wrong type this returns
                     * -1 which becomes 0 below. This matches editor behavior. */
                    p.default = p.values.indexOf(p.default);
                }
                if (p.default < 0 || p.default >= p.values.length) {
                    p.default = 0;
                }
            } else {
                /* There's no index value that makes sense */
                p.default = undefined;
            }
        } else if (
            (p.type === Type.Color ||
                p.type === Type.Vector2 ||
                p.type === Type.Vector3 ||
                p.type === Type.Vector4) &&
            Array.isArray(p.default)
        ) {
            /* Defaults provided by user code are currently always a standard
             * array, but become a typed array on the property */
            p.default = Float32Array.from(p.default);
        } else if (p.type === Type.Record && p.record) {
            p.default = new p.record();
            resetComponentProperties(p.default);
        } else if (p.default === undefined) {
            const cloner = p.cloner ?? defaultPropertyCloner;
            p.default = cloner.clone(p.type, _componentDefaults.get(p.type));
        }
        ctor.prototype[name] = p.default;
    }
}

/**
 * Determines a fixed order for property attributes used for deserialization.
 *
 * @param ctor The component class
 */
function _setPropertyOrder(ctor: ComponentConstructor | PropertyRecord) {
    ctor._propertyOrder = ctor.hasOwnProperty('Properties')
        ? Object.keys(ctor.Properties!).sort()
        : [];
}
